import type { AuthState } from './auth.types'
import { getAuth, signOut } from 'firebase/auth'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { isTokenExpired, isTokenValid } from '@/shared/lib/auth'
import { STORAGE_KEYS } from '@/shared/lib/storage'

const initialState: AuthState = {
  token: null,
  tokenExpiry: null,
  region: null,
  projectId: null,
  destinationId: null,
  isLoading: false,
  error: null,
}

interface AuthStore extends AuthState {
  // Core auth actions
  logout: () => Promise<void>

  // State management
  setLoading: (isLoading: boolean) => void
  setError: (error: Error | null) => void
  resetError: () => void

  // Auth state management
  setAuthState: (state: Partial<AuthState>) => void
  setTokenWithExpiry: (token: string, expiry?: number) => void

  // Authentication checks
  isAuthenticated: () => boolean
}

/**
 * Auth store using Zustand with persistence
 * Manages authentication state and provides actions for login, logout, etc.
 * Uses localStorage persistence to maintain auth state across page reloads
 * Includes token expiry management and automatic refresh
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Sets the authentication token with expiry
       * @param token The token to set
       * @param expiry The expiry time in UNIX (seconds) (default: 90 days)
       */
      setTokenWithExpiry: (token: string, expiry?: number) => {
        const tokenExpiry = expiry || Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 90

        set({
          token,
          tokenExpiry,
        })
      },

      /**
       * Logout and clear auth state
       */
      logout: async () => {
        try {
          const auth = getAuth()
          await signOut(auth)

          localStorage.removeItem(STORAGE_KEYS.AUTH_STATE)

          set({
            ...initialState,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          console.error('Logout error:', error)
          set({
            error: error instanceof Error ? error : new Error('Failed to logout'),
          })
        }
      },

      /**
       * Reset error state
       */
      resetError: () => {
        set({ error: null })
      },

      /**
       * Set error state
       */
      setError: (error) => {
        set({ error })
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading) => {
        set({ isLoading })
      },

      /**
       * Set specific auth state properties
       */
      setAuthState: (state: Partial<AuthState>) => {
        set(state)
      },

      /**
       * Checks if the user is authenticated
       */
      isAuthenticated: (): boolean => {
        const { token, tokenExpiry } = get()
        return isTokenValid(token) && !isTokenExpired(tokenExpiry)
      },

    }),
    {
      name: STORAGE_KEYS.AUTH_STATE,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        token: state.token,
        tokenExpiry: state.tokenExpiry,
        region: state.region,
        projectId: state.projectId,
        destinationId: state.destinationId,
      }),
    },
  ),
)
